import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { ExchangeItem } from 'src/app/models/exchange.model';
import { Skill, SKILLS } from 'src/app/models/skill.model';
import { VillagerState } from 'src/app/state/villager.state';
import * as firebase from 'firebase';
import 'firebase/firestore';
import { CreateExchange, UpdateExchange } from 'src/app/state/app.actions';
import {
  ToastController,
  AlertController,
  LoadingController,
  ModalController,
} from '@ionic/angular';
import { Observable } from 'rxjs';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { AnalyticsService } from 'src/app/analytics.service';
import { VillageState } from 'src/app/state/village.state';
import { EXCHANGE_KARMA } from 'src/app/app.config';
import { JoinedCircle } from 'src/app/models/circle.model';
import { AppState } from 'src/app/state/app.state';
import { StoredDocument } from 'src/app/models/post-core.model';
import { PostFileUploadComponent } from 'src/app/shared/post-file-upload/post-file-upload.component';
import { RecipientNotificationSettings } from 'src/app/models/notification-settings';
import { CreatePostHelpersService } from 'src/app/services/create-post-helpers.service';
import { PostMentionableInputComponent } from 'src/app/shared/post-mentionable-input/post-mentionable-input.component';
import { VillagerActions } from 'src/app/state/villager.actions';

@Component({
  selector: 'app-create-exchange-modal',
  templateUrl: './create-exchange-modal.page.html',
  styleUrls: ['./create-exchange-modal.page.scss'],
})
export class CreateExchangeModalPage implements OnInit, AfterViewInit {
  skills: Skill[] = SKILLS;
  placeholder: string;
  component = 'create-exchange-modal';

  tmpImageUrls: string[] = [];
  tmpDocUrls: StoredDocument[] = [];

  showFeeling = false;
  showPhotos = false;
  showTopics = false;

  exchangeForm = new FormGroup({
    title: new FormControl('', [Validators.required]),
    itemType: new FormControl('', [Validators.required]),
    skill: new FormControl('', [Validators.required]),
  });

  customActionSheetOptions: any = {
    header: 'Select a Skill',
    subHeader: `It helps people in your village support you quickly.`,
  };

  mentionables: string[] = [];

  loading: any;
  errorMessage = null;
  successMessage = null;
  submitting = false;

  topics: string[] = [];
  assignedCircles: JoinedCircle[] = [];

  EXCHANGE_KARMA = EXCHANGE_KARMA;

  get title() {
    return this.exchangeForm.get('title');
  }

  get itemType() {
    return this.exchangeForm.get('itemType');
  }

  get image() {
    return this.exchangeForm.get('image');
  }

  get skill() {
    return this.exchangeForm.get('skill');
  }

  @Select(VillageState.villageName) villageName$: Observable<string>;
  @Select(VillageState.showKarma) showKarma$: Observable<boolean>;
  @Select(AppState.postCirclesAssigned) postCirclesAssigned$: Observable<
    JoinedCircle[]
  >;

  @Input() type: 'OFFERING' | 'REQUEST';
  @Input() edit = false;
  @Input() description: string;
  @Input() itemToEdit: ExchangeItem;
  @Output() done = new EventEmitter();

  @ViewChild(PostMentionableInputComponent)
  mentionInput: PostMentionableInputComponent;
  @ViewChild(PostFileUploadComponent) fileUpload: PostFileUploadComponent;

  constructor(
    private store: Store,
    public modalCtrl: ModalController,
    private toastCtrl: ToastController,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private utils: UtilitiesService,
    private analytics: AnalyticsService,
    private helpers: CreatePostHelpersService
  ) {}

  ngAfterViewInit(): void {
    if (this.edit === true) {
      console.log('edit post: ', this.itemToEdit._UID);
      this.mentionInput.setBody(this.utils.removeExistingTags(this.itemToEdit.BODY), this.itemToEdit._MENTION_UIDS);
    } else {
      if (this.description) {
        this.mentionInput.setBody(this.utils.removeExistingTags(this.description), []);
      }
    }
  }

  ngOnInit() {
    this.postCirclesAssigned$.subscribe((assignedCircles: JoinedCircle[]) => {
      this.assignedCircles = assignedCircles;
    });

    this.itemType.setValue(this.type);
    this.topics.push(`#${this.type.toLowerCase()}`);

    if (this.edit) {
      this.topics = [...this.itemToEdit.TOPICS];
      this.title.setValue(this.itemToEdit.TITLE);
      this.skill.setValue(this.itemToEdit.SKILL);
      this.tmpImageUrls = [...this.itemToEdit.IMAGE_PATHS];
      this.tmpDocUrls = [...this.itemToEdit.DOCUMENT_PATHS];
      this.assignedCircles = [...this.itemToEdit.CIRCLES];
    }

    switch (this.type) {
      case 'OFFERING':
        this.placeholder = 'Give your offering a title';
        break;
      case 'REQUEST':
        this.placeholder = 'Give your request a title';
        break;
      default:
        this.placeholder = 'Give your exchange a title';
    }

    this.loadMentionables();
  }

  onSetTopics(topics: string[]) {
    this.topics = topics;
  }

  handleDeletePhoto(event, index: number) {
    this.tmpImageUrls.splice(index, 1);
  }

  handleDeleteDocument(index: number) {
    this.tmpDocUrls.splice(index, 1);
  }

  async createExchangeItem() {
    const { BODY } = this.mentionInput.parseMentionsAndBody();
    if (!BODY || BODY.trim().length === 0) {
      this.missingDataError(
        'Missing description. Please add a description and try again.'
      );
    } else {
      try {
        let photoPaths = [];
        let documentPaths = [];
        if (this.fileUpload) {
          photoPaths = await this.fileUpload.uploadPhotos();
          documentPaths = await this.fileUpload.uploadDocuments();
        }
        this.save(photoPaths, documentPaths);
      } catch (e) {
        this.analytics.logEvent('exchange_create_error', {
          error: e,
        });
        alert('Error creating post');
      }
    }
  }

  async updateExchange() {
    await this.presentLoading();
    this.update();
  }

  async update() {
    this.analytics.logEvent('exchange_update', {
      exchangeId: this.itemToEdit._UID,
      type: this.type,
    });
    let newPhotoPaths = [];
    let newDocumentPaths: StoredDocument[] = [];
    try {
      if (this.fileUpload) {
        newPhotoPaths = await this.fileUpload.uploadPhotos();
        newDocumentPaths = await this.fileUpload.uploadDocuments();
      }
    } catch (e) {
      alert('Error updating photos & documents');
      this.analytics.logError('error_updating_post_files', this.component, e);
    }
    let allImages = [...this.tmpImageUrls];
    let allDocuments = [...this.tmpDocUrls];
    if (newPhotoPaths.length > 0) {
      allImages = this.tmpImageUrls.concat(newPhotoPaths);
    }
    if (newDocumentPaths.length > 0) {
      allDocuments = this.tmpDocUrls.concat(newDocumentPaths);
    }

    const CIRCLE_UIDS = this.assignedCircles.map((x) => x.UID);
    const RECIPIENT_NOTIFICATION_SETTINGS: RecipientNotificationSettings =
      this.helpers.getNotificationRecipientsForCircle(CIRCLE_UIDS);

    let exchange: ExchangeItem = {
      ...this.itemToEdit,
      SKILL: this.skill.value,
      TITLE: this.title.value,
      IMAGE_PATHS: allImages,
      DOCUMENT_PATHS: allDocuments,
      UPDATED_AT: firebase.default.firestore.FieldValue.serverTimestamp(),
      TOPICS: this.topics,
      RECIPIENT_NOTIFICATION_SETTINGS,
      CIRCLES: this.assignedCircles,
      CIRCLE_UIDS,
    };


    const { _MENTION_UIDS, BODY } = this.mentionInput.parseMentionsAndBody();
    exchange._MENTION_UIDS = _MENTION_UIDS;
    exchange.BODY = this.utils.parseLinksFromText(BODY);

    this.store.dispatch(new UpdateExchange({ post: exchange })).subscribe(
      () => {
        this.analytics.logEvent('exchange_update_success', {
          type: this.type,
        });
        this.presentToast('Successfully updated exchange.', false);
        this.exchangeForm.reset();
        this.modalCtrl.dismiss({ action: null });
        this.loading.dismiss();
        this.loading = null;
      },
      (err) => {
        this.analytics.logEvent('exchange_update_error', {
          exchangeId: this.itemToEdit._UID,
          type: this.type,
          error: err,
        });
        console.error(err);
        this.showErrorAlert();
        this.loading.dismiss();
        this.loading = null;
      }
    );
  }

  async showDeletePhotoConfirm() {
    const alert = await this.alertCtrl.create({
      header: 'Careful!',
      message: `Are you sure you want to delete images?`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'dark',
          handler: () => {},
        },
        {
          text: 'Delete',
          role: 'delete',
          cssClass: 'danger',
          handler: () => {
            this.presentLoading();
            this.update();
          },
        },
      ],
    });

    await alert.present();
  }

  async save(imagePaths: string[], documentPaths: StoredDocument[]) {
    this.analytics.logEvent('exchange_save_new', {
      type: this.type,
    });
    await this.presentLoading();
    const uid = this.store.selectSnapshot(VillagerState.uid);
    const firstName = this.store.selectSnapshot(VillagerState.firstName);
    const lastName = this.store.selectSnapshot(VillagerState.lastName);
    const CIRCLE_UIDS = this.assignedCircles.map((x) => x.UID);
    const RECIPIENT_NOTIFICATION_SETTINGS: RecipientNotificationSettings =
      this.helpers.getNotificationRecipientsForCircle(CIRCLE_UIDS);

    let newExchange: ExchangeItem = {
      _CREATOR_UID: uid,
      _CREATOR_FIRST_NAME: firstName,
      _CREATOR_LAST_NAME: lastName,
      TITLE: this.title.value,
      _TYPE: 'EXCHANGE',
      _SERVER_TIMESTAMP:
        firebase.default.firestore.FieldValue.serverTimestamp(),
      _CREATED_AT: new Date(),
      _EXCHANGE_TYPE: this.itemType.value,
      BODY: null,
      IMAGE_PATHS: imagePaths,
      DOCUMENT_PATHS: documentPaths,
      AMOUNT: -1,
      SKILL: this.skill.value,
      UPDATED_AT: firebase.default.firestore.FieldValue.serverTimestamp(),
      INTERESTS: [],
      PARTICIPANT_UIDS: [uid],
      PARTICIPANT_READ_RECEIPTS: [{ UID: uid, LAST_READ_MESSAGE_COUNT: 0 }],
      RECIPIENT_NOTIFICATION_SETTINGS,
      REPORTED_BY: [],
      ACCEPTED: false,
      SUPPORTER_UID: null,
      SUPPORTER_FIRST_NAME: null,
      SUPPORTER_LAST_NAME: null,
      ACCEPTED_AMOUNT: null,
      SUPPORTER_COMPLETED: false,
      CREATOR_COMPLETED: false,
      MESSAGE_COUNT: 0,
      COINS_TRANSFERRED: false,
      COMPLETED: false,
      TOPICS: this.topics,
      ARCHIVED: false,
      CIRCLES: this.assignedCircles,
      CIRCLE_UIDS,
      _MENTION_UIDS: []
    };

    const { _MENTION_UIDS, BODY } = this.mentionInput.parseMentionsAndBody();
    newExchange._MENTION_UIDS = _MENTION_UIDS;
    newExchange.BODY = this.utils.parseLinksFromText(BODY);

    this.store
      .dispatch(new CreateExchange({ exchangeItem: newExchange }))
      .subscribe(
        (result) => {
          this.store.dispatch(new VillagerActions.MarkOnboardedTaskComplete({ villagerId: uid, field: "HAS_CREATED_POST" }))
          this.analytics.logEvent('exchange_save_new_success', {
            type: this.type,
          });
          this.presentToast('Successfully created exchange.', true);
          this.exchangeForm.reset();
          this.modalCtrl.dismiss({ action: 'create-close' });
          this.loading.dismiss();
          this.loading = null;
        },
        (err) => {
          this.analytics.logEvent('exchange_save_new_error', {
            type: this.type,
            error: err,
          });
          console.error(err);
          this.showErrorAlert();
          this.loading.dismiss();
          this.loading = null;
        }
      );
  }

  async presentToast(header: string, update = false) {
    let message;
    if (update) {
      message = `You earned ${EXCHANGE_KARMA} karma!`;
    }
    const toast = await this.toastCtrl.create({
      header: header,
      message,
      color: 'primary',
      duration: 3000,
      position: 'top',
    });
    toast.present();
  }
  async presentLoading() {
    if (!this.loading) {
      this.loading = await this.loadingCtrl.create({
        duration: 15000,
        backdropDismiss: true,
      });
      return this.loading.present();
    }
  }

  async showErrorAlert() {
    const alert = await this.alertCtrl.create({
      header: 'Error',
      message:
        'We ran into an error creating the post. Please check that everything is filled out and try again.',
      buttons: ['OK'],
    });

    await alert.present();
  }

  async missingDataError(message: string) {
    const alert = await this.alertCtrl.create({
      header: 'Error',
      message,
      buttons: ['OK'],
    });

    await alert.present();
  }

  async loadMentionables() {
    let tmpMentionables = [];

    const allUnblockedVillagers = this.store.selectSnapshot(
      VillagerState.allUnblockedVillagersExcludeCurrent
    );
    tmpMentionables = [
      ...allUnblockedVillagers.map((villager) => villager._UID),
    ];

    this.mentionables = [...new Set(tmpMentionables)];
    console.log('[POST BODY INPUT] Mentionables: ', this.mentionables);
  }
}
